/**
 * Shared agent log helpers — main thread ring buffer + kernel-worker emit filters.
 * Keep in sync with docs: docs/src/hal/overview.md (Agent log) and AGENTS.md (Browser agent hooks).
 */

/** @typedef {"error"|"warn"|"info"|"debug"} AgentLogLevel */
/** @typedef {"error"|"warn"|"info"|"debug"|"off"} AgentChannelSetting */

/** Bump when __wasmos agent-hook shape or semantics change incompatibly. */
export const AGENT_HOOKS_PROTOCOL = 3;

/** sessionStorage key for `setAgentLogConfig({ persistToSessionStorage: true })`. */
export const AGENT_SESSION_STORAGE_KEY = "wasmos_agent_hooks_v1";

const LEVEL_RANK = /** @type {const} */ ({ error: 0, warn: 1, info: 2, debug: 3 });

/**
 * Severity filter: include entries at least as severe as `minLevel` (error most severe, debug least).
 * @param {AgentLogLevel} minLevel
 * @param {AgentLogLevel} entryLevel
 */
export function agentEntryMeetsMinSeverity(minLevel, entryLevel) {
  const th = LEVEL_RANK[/** @type {keyof typeof LEVEL_RANK} */ (minLevel)] ?? 2;
  const er = LEVEL_RANK[/** @type {keyof typeof LEVEL_RANK} */ (entryLevel)] ?? 2;
  return er <= th;
}

/**
 * @param {AgentChannelSetting | undefined} setting
 * @param {AgentLogLevel} level
 */
export function channelAccepts(setting, level) {
  if (setting === undefined || setting === "off") return false;
  const maxRank = LEVEL_RANK[/** @type {keyof typeof LEVEL_RANK} */ (setting)] ?? 2;
  const entryRank = LEVEL_RANK[level] ?? 2;
  return entryRank <= maxRank;
}

export const DEFAULT_AGENT_LOG_CONFIG = {
  channels: {
    lifecycle: /** @type {AgentChannelSetting} */ ("info"),
    worker: /** @type {AgentChannelSetting} */ ("warn"),
    syscall: /** @type {AgentChannelSetting} */ ("off"),
  },
  mirrorSyscallToConsole: false,
  maxEntries: 500,
  maxMsgLen: 2048,
  maxDebugPerSecond: 80,
};

/**
 * @param {string} search
 * @returns {Partial<typeof DEFAULT_AGENT_LOG_CONFIG> & { channels?: Record<string, AgentChannelSetting> }}
 */
export function parseWasmosAgentSearchParam(search) {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const raw = params.get("wasmos_agent");
  const mirror = params.get("wasmos_mirror_syscall") === "1" || params.get("wasmos_mirror_syscall") === "true";
  if (!raw) {
    return mirror ? { mirrorSyscallToConsole: true } : {};
  }
  const trimmed = raw.trim();
  if (trimmed === "quiet") {
    return {
      channels: { lifecycle: "off", worker: "off", syscall: "off" },
      mirrorSyscallToConsole: false,
    };
  }
  if (trimmed === "trace") {
    return {
      channels: { lifecycle: "info", worker: "warn", syscall: "debug" },
      mirrorSyscallToConsole: mirror,
    };
  }
  /** @type {Record<string, AgentChannelSetting>} */
  const channels = { lifecycle: "off", worker: "off", syscall: "off" };
  let sawAny = false;
  for (const part of trimmed.split(",")) {
    const p = part.trim();
    if (!p) continue;
    if (p === "mirror_console" || p === "mirror") {
      continue;
    }
    const colon = p.indexOf(":");
    if (colon === -1) {
      sawAny = true;
      if (p === "lifecycle" || p === "worker" || p === "syscall") {
        channels[p] = p === "syscall" ? "debug" : p === "worker" ? "warn" : "info";
      }
      continue;
    }
    const key = p.slice(0, colon);
    const val = p.slice(colon + 1);
    if (key === "lifecycle" || key === "worker" || key === "syscall") {
      sawAny = true;
      if (val === "off" || val === "error" || val === "warn" || val === "info" || val === "debug") {
        channels[key] = val;
      }
    }
  }
  if (!sawAny) return {};
  return {
    channels,
    mirrorSyscallToConsole: mirror || trimmed.includes("mirror_console"),
  };
}
